import { useState, useMemo, useEffect, useRef, useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { X, User, Truck, Utensils, Package, CheckCircle, XCircle, MapPin, ExternalLink, Navigation, Clock, Trash2, Edit, Copy, Check, UserMinus, UserPlus, Minus, Plus, AlertTriangle } from '@/ui/icons'
import {
  Badge,
  Button,
  ConfirmationDialog,
  Label,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/ui/components'
import { Link } from 'react-router-dom'
import { useTripDetailPanel } from '../contexts/TripDetailPanelContext'
import { useTripOverviewData } from '@/modules/trips/hooks/useTripOverviewData'
import { useTrip, useMemberOperations } from '@/modules/trips'
import { useAuth } from '@/modules/auth'
import { useConfirm } from '@/shared/hooks'
import { useGearNeeds } from '@/modules/gear/hooks/useGearNeeds'
import { useRealtimeGearNeeds } from '@/modules/gear/hooks/useRealtimeGearNeeds'
import { useMealNeeds } from '@/modules/meals/hooks/useMealNeeds'
import { useRealtimeMealNeeds } from '@/modules/meals/hooks/useRealtimeMealNeeds'
import { AddGearNeedForm } from '@/modules/gear/components/AddGearNeedForm'
import { AddMealForm } from '@/modules/meals/components/AddMealForm'
import { useTripAnnouncements } from '@/modules/announcements'
import { AnnouncementComposeForm } from './AnnouncementsSection'
import { TripFeedComposeForm } from './TripFeedSection'
import { calculateWaypointDistance } from '@/modules/trips/utils/itineraryDistanceUtils'
import { Card, CardContent } from '@/ui/design-system'
import { formatCoordinatesForCopy } from '@/modules/trips/utils/coordinateUtils'
import { getMealContributorName } from '@/modules/meals/utils/contributors'
import { formatDateForDisplay } from '@/modules/trips/utils/dateHelpers'
import { getUserProfileUrl, getUserBannerUrl } from '@/shared/utils/userImages'
import { canEditTrip } from '@/shared/utils/auth'
import { cn } from '@/shared/utils'
import type { MemberResponse } from '@/shared/api'
import { WaypointTimeInput } from '@/modules/trips/components/forms/WaypointTimeInput'
import { WaypointNotesEditor } from '@/modules/trips/components/forms/WaypointNotesEditor'
import { WaypointTypeDropdown } from '@/modules/trips/components/waypoints/WaypointTypeDropdown'
import { getWaypointTypeLabel, getWaypointTypeIconWithCustom } from '@/pages/itinerary-builder/utils/waypointTypeStyles'
import { normalizeTimeToHHMM } from '@/modules/trips/utils/meetupWaypointSync'
import { formatTimeForDisplay, calculateDurationMinutes, formatDuration } from '@/modules/trips/utils/timeCalculations'
import type { POIType } from '@/pages/itinerary-builder/utils/waypointTypeStyles'
import { MilitaryRoleSelector } from '@/modules/trips/components/MilitaryRoleSelector'
import { MILITARY_ROLES, getRoleLabel } from '@/shared/api/militaryRoles'
import type { MilitaryRole } from '@/shared/api/militaryRoles'
import { getGearQuantities } from '@/shared/api/gearNeeds'
import { TripFeedApi } from '@/shared/api'
import type { CreateTripFeedPostDto } from '@/shared/api/types'

const PANEL_WIDTH_CLASS = 'w-full md:w-[360px]'
/** Drawer uses top safe area; desktop panel is inset via fixed parent (TripsLayout). */
const PANEL_SAFE_AREA = 'pb-[env(safe-area-inset-bottom)]'
const DRAWER_SAFE_AREA = 'pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]'
const CLOSE_BUTTON_CLASS = 'min-h-[44px] min-w-[44px]'

function getMemberDisplayName(member: MemberResponse): string {
  if (member.guest_name) return member.guest_name
  const name = member.user?.user_metadata?.full_name ?? member.user?.email
  return name ?? 'Crew member'
}

interface TripDetailRightPanelProps {
  tripId: string
  /** When "drawer", panel is shown in a bottom sheet on mobile; affects padding and border. */
  variant?: 'panel' | 'drawer'
}

export function TripDetailRightPanel({ tripId, variant = 'panel' }: TripDetailRightPanelProps) {
  const isDrawer = variant === 'drawer'
  const panelRootClass = cn(
    PANEL_WIDTH_CLASS,
    isDrawer ? DRAWER_SAFE_AREA : PANEL_SAFE_AREA,
    isDrawer && 'border-l-0 px-4'
  )
  const panelAsideClass = cn(
    'flex flex-col flex-shrink-0 h-full overflow-hidden',
    panelRootClass,
    isDrawer
      ? 'border-l-0 bg-earth-50'
      : 'rounded-2xl bg-earth-50/85 backdrop-blur-xl border border-earth-200/60 shadow-float'
  )
  const { selectedDetail, setSelectedDetail, closePanel, onWaypointDeletedRef } = useTripDetailPanel()
  const { waypoints: allWaypoints, routeSchedule, updateWaypoint, deleteWaypoint, refresh: refreshOverview, isLoading: isOverviewLoading } = useTripOverviewData(tripId)
  const { trip, loadTrip } = useTrip(tripId)
  const queryClient = useQueryClient()
  const { user, getAccessToken } = useAuth()

  const handleTripMemberUpdate = useCallback(async () => {
    await loadTrip()
    queryClient.invalidateQueries({ queryKey: ['trip', tripId] })
  }, [loadTrip, queryClient, tripId])
  const { removeMember, updateMilitaryRole, updateMemberRole } = useMemberOperations()
  const { confirm, confirmState, close, setLoading: setConfirmLoading } = useConfirm()
  const {
    gearNeeds,
    createGearNeed,
    updateGearNeed,
    deleteGearNeed,
    claimGearNeed,
    updateGearClaim,
    unclaimGearNeed,
    refreshGearNeeds,
    isCreating,
    isUpdating,
    isDeleting,
    isClaiming
  } = useGearNeeds(tripId)
  useRealtimeGearNeeds({
    tripId,
    enabled: !!tripId && !!user,
    onGearNeedsUpdate: refreshGearNeeds
  })
  const {
    meals,
    createMeal,
    updateMeal,
    deleteMeal,
    refreshMeals,
    optOutOfMeal,
    optInToMeal,
    isOptingOut: isMealOptingOut,
    isCreating: isMealCreating,
    isUpdating: isMealUpdating,
    isDeleting: isMealDeleting
  } = useMealNeeds(tripId, { skip: false })
  useRealtimeMealNeeds({
    tripId,
    enabled: !!tripId && !!user,
    onMealNeedsUpdate: refreshMeals
  })
  const { createAnnouncement } = useTripAnnouncements(tripId)
  const tripFeedApi = useMemo(() => new TripFeedApi(getAccessToken), [getAccessToken])
  const createPost = useCallback(
    async (payload: CreateTripFeedPostDto) => tripFeedApi.createPost(tripId, payload),
    [tripFeedApi, tripId]
  )
  const isOwner = Boolean(trip && user && canEditTrip(trip, user))
  const [isUpdatingWaypoint, setIsUpdatingWaypoint] = useState(false)
  const [isEditingGear, setIsEditingGear] = useState(false)
  const [isEditingMeal, setIsEditingMeal] = useState(false)
  const [copiedLocation, setCopiedLocation] = useState(false)
  const [isUpdatingMilitaryRole, setIsUpdatingMilitaryRole] = useState(false)
  const [isUpdatingTripRole, setIsUpdatingTripRole] = useState(false)
  const [isEditingWaypointName, setIsEditingWaypointName] = useState(false)
  const [waypointNameDraft, setWaypointNameDraft] = useState('')
  const [gearClaimQuantity, setGearClaimQuantity] = useState<number | ''>('')

  useEffect(() => {
    setIsEditingGear(false)
  }, [selectedDetail?.type === 'gear' ? selectedDetail.id : null])

  useEffect(() => {
    setIsEditingMeal(false)
  }, [selectedDetail?.type === 'meal' ? selectedDetail.id : null])

  // When opening the panel for a waypoint that isn't in our list yet (e.g. just added),
  // refetch overview data once so the panel can show it without a full page refresh.
  const refetchedForWaypointIdRef = useRef<string | null>(null)
  useEffect(() => {
    if (
      selectedDetail?.type !== 'waypoint' ||
      !selectedDetail.id ||
      isOverviewLoading
    ) return
    const waypoint = allWaypoints.find(w => w.id === selectedDetail.id)
    if (waypoint) {
      refetchedForWaypointIdRef.current = null
      return
    }
    if (refetchedForWaypointIdRef.current === selectedDetail.id) return
    refetchedForWaypointIdRef.current = selectedDetail.id
    refreshOverview({ cache: 'no-store' })
  }, [selectedDetail?.type, (selectedDetail && 'id' in selectedDetail ? selectedDetail.id : undefined), allWaypoints, isOverviewLoading, refreshOverview])

  // Same pattern for gear: when the panel opens for a newly added gear item that isn't
  // in gearNeeds yet, force a refresh so GearSummarySection updates without a page reload.
  const refetchedForGearIdRef = useRef<string | null>(null)
  useEffect(() => {
    if (selectedDetail?.type !== 'gear' || !selectedDetail.id) return
    const gear = gearNeeds?.find(g => g.id === selectedDetail.id)
    if (gear) {
      refetchedForGearIdRef.current = null
      return
    }
    if (refetchedForGearIdRef.current === selectedDetail.id) return
    refetchedForGearIdRef.current = selectedDetail.id
    refreshGearNeeds()
  }, [selectedDetail?.type, (selectedDetail && 'id' in selectedDetail ? selectedDetail.id : undefined), gearNeeds, refreshGearNeeds])

  const scheduledRoutes = useMemo(
    () => (routeSchedule ? routeSchedule.days.flatMap((d) => d.routes) : []),
    [routeSchedule]
  )

  const sortedWaypoints = useMemo(() => {
    return [...allWaypoints].sort((a, b) => {
      const dayA = a.trip_day ?? 1
      const dayB = b.trip_day ?? 1
      if (dayA !== dayB) return dayA - dayB
      return (a.order_index ?? 0) - (b.order_index ?? 0)
    })
  }, [allWaypoints])

  const { waypoint, dayNumber, distanceFromPrevious } = useMemo(() => {
    if (!selectedDetail || selectedDetail.type !== 'waypoint') {
      return { waypoint: null, dayNumber: 1, distanceFromPrevious: null as string | null }
    }
    const waypoint = sortedWaypoints.find(w => w.id === selectedDetail.id)
    if (!waypoint) return { waypoint: null, dayNumber: 1, distanceFromPrevious: null }

    const index = sortedWaypoints.indexOf(waypoint)
    const dayNumber = waypoint.trip_day ?? 1
    const previous = index > 0 ? sortedWaypoints[index - 1] : null
    const dist = previous
      ? calculateWaypointDistance(previous, waypoint, scheduledRoutes)
      : null
    return {
      waypoint,
      dayNumber,
      distanceFromPrevious: dist ? dist.distanceFormatted : null
    }
  }, [selectedDetail, sortedWaypoints, scheduledRoutes])

  useEffect(() => {
    if (selectedDetail?.type === 'waypoint' && waypoint) {
      setWaypointNameDraft(waypoint.name ?? '')
      setIsEditingWaypointName(false)
    }
  }, [
    selectedDetail?.type,
    selectedDetail && 'id' in selectedDetail ? selectedDetail.id : undefined,
    waypoint?.id,
    waypoint?.name
  ])

  const selectedMember = useMemo(() => {
    if (!selectedDetail || selectedDetail.type !== 'crew' || !trip?.members) return null
    return trip.members.find((m) => m.id === selectedDetail.id) ?? null
  }, [selectedDetail, trip?.members])

  const selectedGear = useMemo(() => {
    if (!selectedDetail || selectedDetail.type !== 'gear') return null
    return gearNeeds?.find((g) => g.id === selectedDetail.id) ?? null
  }, [selectedDetail, gearNeeds])

  const selectedMeal = useMemo(() => {
    if (!selectedDetail || selectedDetail.type !== 'meal') return null
    return meals?.find((m) => m.id === selectedDetail.id) ?? null
  }, [selectedDetail, meals])

  useEffect(() => {
    if (selectedDetail?.type === 'gear' && selectedGear && user) {
      const { userConfirmedQuantity, remainingQuantity } = getGearQuantities(selectedGear, user.id)
      const base = userConfirmedQuantity > 0 ? userConfirmedQuantity : ''
      const max = Math.max(1, remainingQuantity + userConfirmedQuantity)
      const numericBase = typeof base === 'number' ? base : ''
      const clamped =
        typeof numericBase === 'number' ? Math.min(numericBase, max) : ''
      setGearClaimQuantity(clamped)
    } else {
      setGearClaimQuantity('')
    }
  }, [selectedDetail?.type, selectedDetail && 'id' in selectedDetail ? selectedDetail.id : undefined, selectedGear, user])

  if (!selectedDetail) return null

  if (selectedDetail.type === 'crew' && selectedMember) {
    const displayName = getMemberDisplayName(selectedMember)
    const vehicleName = selectedMember.primary_vehicle
      ? [selectedMember.primary_vehicle.year, selectedMember.primary_vehicle.make, selectedMember.primary_vehicle.model].filter(Boolean).join(' ')
      : null
    const avatarUrl = selectedMember.member_type === 'full_member' && selectedMember.user_profile?.profile_image_path
      ? getUserProfileUrl(selectedMember.user_profile.profile_image_path)
      : null
    const bannerUrl = selectedMember.member_type === 'full_member' && selectedMember.user_profile?.banner_image_path
      ? getUserBannerUrl(selectedMember.user_profile.banner_image_path)
      : null
    const bio = selectedMember.member_type === 'full_member' ? selectedMember.user_profile?.bio : null
    const location = selectedMember.member_type === 'full_member' ? selectedMember.user_profile?.location : null
    const hasFullProfile = selectedMember.member_type === 'full_member' && !!selectedMember.user_id
    const initials = displayName.charAt(0).toUpperCase() + (displayName.includes(' ') ? displayName.split(' ').pop()?.charAt(0).toUpperCase() ?? '' : '')

    const ownerCount = trip?.members?.filter((m) => m.role === 'owner').length ?? 0
    const isLastOwner = selectedMember.role === 'owner' && ownerCount <= 1
    const canRemoveMember = isOwner && !isLastOwner

    const currentUserMember = trip?.members?.find((m) => m.user_id === user?.id) ?? null
    const isSelf = selectedMember.user_id === user?.id
    const canAssignMilitaryRole =
      selectedMember.member_type === 'full_member' &&
      (isOwner ||
        currentUserMember?.military_role === 'trip_commander' ||
        isSelf)
    const assignedByOthers = (trip?.members ?? [])
      .filter((m) => m.id !== selectedMember.id)
      .map((m) => m.military_role)
      .filter((r): r is MilitaryRole => r != null)
    const availableRoles = MILITARY_ROLES.filter((r) => r.value != null && !assignedByOthers.includes(r.value))

    const handleUpdateMilitaryRole = async (role: MilitaryRole) => {
      setIsUpdatingMilitaryRole(true)
      try {
        await updateMilitaryRole(tripId, selectedMember.id, role)
        await handleTripMemberUpdate()
      } finally {
        setIsUpdatingMilitaryRole(false)
      }
    }

    const handleRemoveMember = () => {
      confirm(
        {
          title: 'Remove from trip',
          description: 'Are you sure you want to remove this member from the trip?',
          variant: 'destructive',
          confirmText: 'Remove',
        },
        async () => {
          setConfirmLoading(true)
          try {
            await removeMember(tripId, selectedMember.id)
            await handleTripMemberUpdate()
            closePanel()
          } finally {
            setConfirmLoading(false)
          }
        }
      )
    }

    const canChangeTripRole =
      isOwner &&
      !isSelf &&
      selectedMember.member_type === 'full_member'
    const handleUpdateTripRole = async (role: 'owner' | 'member') => {
      if (selectedMember.role === role) return
      setIsUpdatingTripRole(true)
      try {
        await updateMemberRole(tripId, selectedMember.id, { role })
        await handleTripMemberUpdate()
      } finally {
        setIsUpdatingTripRole(false)
      }
    }

    return (
      <>
      <aside
        className={panelAsideClass}
      >
        <div className="flex items-center justify-between p-4 border-b border-earth-200">
          <h2 className="text-lg font-semibold text-foreground">Crew Details</h2>
          <Button variant="ghost" size="icon" className={CLOSE_BUTTON_CLASS} onClick={closePanel} aria-label="Close panel">
            <X className="h-5 w-5" />
          </Button>
        </div>
        <div className="flex-1 overflow-y-auto flex flex-col">
          {bannerUrl && (
            <div className="relative w-full h-28 flex-shrink-0 overflow-hidden bg-adventure-100">
              <img
                src={bannerUrl}
                alt=""
                className="w-full h-full object-cover"
              />
            </div>
          )}
          <div className="p-4 space-y-4">
            <div className="flex items-center gap-3">
              {avatarUrl ? (
                <img src={avatarUrl} alt="" className="h-12 w-12 rounded-full object-cover border-2 border-earth-200" />
              ) : (
                <div className="h-12 w-12 rounded-full bg-adventure-100 text-adventure-800 flex items-center justify-center text-lg font-semibold border-2 border-earth-200">
                  {initials || '?'}
                </div>
              )}
              <div>
                <h3 className="text-base font-medium text-earth-900">{displayName}</h3>
                <Badge variant={selectedMember.role === 'owner' ? 'default' : 'secondary'} className="mt-1 capitalize">
                  {selectedMember.role}
                </Badge>
              </div>
            </div>
            {selectedMember.member_type === 'full_member' && (
              <div className="space-y-2">
                <Label htmlFor="crew-role" className="text-xs font-medium text-earth-500">
                  Crew role
                </Label>
                {canChangeTripRole ? (
                  <Select
                    value={selectedMember.role}
                    onValueChange={(value) => handleUpdateTripRole(value as 'owner' | 'member')}
                    disabled={isUpdatingTripRole}
                  >
                    <SelectTrigger id="crew-role">
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="owner">Owner</SelectItem>
                      <SelectItem value="member">Member</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="text-sm text-earth-900 capitalize">
                    {selectedMember.role}
                  </p>
                )}
              </div>
            )}
            {bio && (
              <Card className="border-earth-200">
                <CardContent className="p-3">
                  <span className="text-xs font-medium text-earth-500">Bio</span>
                  <p className="text-sm text-earth-700 mt-1 whitespace-pre-wrap">{bio}</p>
                </CardContent>
              </Card>
            )}
            {location && (
              <Card className="border-earth-200">
                <CardContent className="p-3 flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-earth-500 flex-shrink-0" />
                  <span className="text-xs font-medium text-earth-500">Location</span>
                  <span className="text-sm text-earth-900 ml-auto">{location}</span>
                </CardContent>
              </Card>
            )}
            {vehicleName && (
              <Card className="border-earth-200">
                <CardContent className="p-3 flex items-center gap-2">
                  <Truck className="h-4 w-4 text-earth-500 flex-shrink-0" />
                  <span className="text-xs font-medium text-earth-500">Vehicle</span>
                  <span className="text-sm text-earth-900 ml-auto">{vehicleName}</span>
                </CardContent>
              </Card>
            )}
            {selectedMember.member_type === 'full_member' && (
              <div className="space-y-2">
                <Label htmlFor="trip-role" className="text-xs font-medium text-earth-500">
                  Trip role
                </Label>
                {canAssignMilitaryRole ? (
                  <MilitaryRoleSelector
                    variant="dropdown"
                    currentRole={selectedMember.military_role as MilitaryRole}
                    availableRoles={availableRoles}
                    onRoleChange={handleUpdateMilitaryRole}
                    disabled={isUpdatingMilitaryRole}
                    isLoading={isUpdatingMilitaryRole}
                  />
                ) : selectedMember.military_role && getRoleLabel(selectedMember.military_role as MilitaryRole) ? (
                  <p className="text-sm text-earth-900">
                    {getRoleLabel(selectedMember.military_role as MilitaryRole)}
                  </p>
                ) : (
                  <p className="text-sm text-earth-500 italic">
                    No role assigned yet
                  </p>
                )}
              </div>
            )}
            {hasFullProfile && (
              <Link
                to={`/users/${selectedMember.user_id}`}
                className="inline-flex items-center gap-2 text-sm font-medium text-adventure-600 hover:text-adventure-700"
              >
                <ExternalLink className="h-4 w-4" />
                View full profile
              </Link>
            )}
            {!selectedMember.parent_member_id && (
              <div className="space-y-2">
                <span className="text-xs font-medium text-earth-500">Tag-alongs</span>
                  {(() => {
                    const tagAlongs = (trip?.members ?? []).filter((m) => m.parent_member_id === selectedMember.id)
                    if (tagAlongs.length === 0) {
                      return (
                        <p className="text-sm text-earth-500">
                          No tag-alongs yet. Add family, friends, or guests riding with this member.
                        </p>
                      )
                    }
                    return (
                      <ul className="space-y-1.5">
                        {tagAlongs.map((ta) => {
                          const taName = ta.guest_name ?? ta.user?.user_metadata?.full_name ?? ta.user?.email ?? 'Tag-along'
                          return (
                            <li
                              key={ta.id}
                              className="flex items-center justify-between rounded-md bg-earth-100/90 px-3 py-2"
                            >
                              <span className="text-sm font-medium">{taName}</span>
                              {isOwner && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0 text-earth-500 hover:text-destructive"
                                  onClick={() => {
                                    confirm(
                                      {
                                        title: 'Remove tag-along',
                                        description: `Remove ${taName} from the trip?`,
                                        variant: 'destructive',
                                        confirmText: 'Remove',
                                      },
                                      async () => {
                                        setConfirmLoading(true)
                                        try {
                                          await removeMember(tripId, ta.id)
                                          await handleTripMemberUpdate()
                                        } finally {
                                          setConfirmLoading(false)
                                        }
                                      }
                                    )
                                  }}
                                    aria-label={`Remove ${taName}`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                            </li>
                          )
                        })}
                      </ul>
                    )
                  })()}
              </div>
            )}
          </div>
          {canRemoveMember && (
            <div className="flex-shrink-0 p-4 border-t border-earth-200">
              <Button
                variant="destructive"
                size="sm"
                className="w-full gap-2"
                onClick={handleRemoveMember}
              >
                <Trash2 className="h-4 w-4" aria-hidden />
                Remove from trip
              </Button>
            </div>
          )}
        </div>
      </aside>
      <ConfirmationDialog
        open={confirmState?.open ?? false}
        onOpenChange={(open) => !open && close()}
        title={confirmState?.title}
        description={confirmState?.description ?? ''}
        confirmText={confirmState?.confirmText}
        cancelText={confirmState?.cancelText}
        variant={confirmState?.variant}
        onConfirm={confirmState?.onConfirm ?? (() => {})}
        isLoading={confirmState?.isLoading}
      />
      </>
    )
  }

  if (selectedDetail.type === 'crew' && !selectedMember) return null

  if (selectedDetail.type === 'gear-add' && user) {
    const handleAddGearSave = async (data: import('@/shared/api/gearNeeds').CreateGearNeedRequest) => {
      const newNeed = await createGearNeed(data)
      closePanel()
      setSelectedDetail({ type: 'gear', id: newNeed.id })
    }

    return (
      <aside
        className={panelAsideClass}
      >
        <div className="flex items-center justify-between p-4 border-b border-earth-200">
          <h2 className="text-lg font-semibold text-foreground">Add Gear Need</h2>
          <Button variant="ghost" size="icon" className={CLOSE_BUTTON_CLASS} onClick={closePanel} aria-label="Close panel">
            <X className="h-5 w-5" />
          </Button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          <AddGearNeedForm
            onSave={handleAddGearSave}
            onCancel={closePanel}
            isLoading={isCreating}
            compact
          />
        </div>
      </aside>
    )
  }

  if (selectedDetail.type === 'gear-add') return null

  if (selectedDetail.type === 'meal-add' && user) {
    const handleAddMealSave = async (data: import('@/shared/api/mealNeeds').CreateMealNeedRequest | import('@/shared/api/mealNeeds').UpdateMealNeedRequest) => {
      const newMeal = await createMeal(data as import('@/shared/api/mealNeeds').CreateMealNeedRequest)
      closePanel()
      setSelectedDetail({ type: 'meal', id: newMeal.id })
    }

    return (
      <aside
        className={panelAsideClass}
      >
        <div className="flex items-center justify-between p-4 border-b border-earth-200">
          <h2 className="text-lg font-semibold text-foreground">Add Meal</h2>
          <Button variant="ghost" size="icon" className={CLOSE_BUTTON_CLASS} onClick={closePanel} aria-label="Close panel">
            <X className="h-5 w-5" />
          </Button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          <AddMealForm
            onSave={handleAddMealSave}
            onCancel={closePanel}
            isLoading={isMealCreating}
            tripId={tripId}
            tripStartDate={trip?.start_date}
            tripEndDate={trip?.end_date}
            compact
          />
        </div>
      </aside>
    )
  }

  if (selectedDetail.type === 'meal-add') return null

  if (selectedDetail.type === 'announcement-add' && user && isOwner) {
    return (
      <aside
        className={panelAsideClass}
      >
        <div className="flex items-center justify-between p-4 border-b border-earth-200">
          <h2 className="text-lg font-semibold text-foreground">Add Announcement</h2>
          <Button variant="ghost" size="icon" className={CLOSE_BUTTON_CLASS} onClick={closePanel} aria-label="Close panel">
            <X className="h-5 w-5" />
          </Button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          <AnnouncementComposeForm
            onCreateAnnouncement={createAnnouncement}
            onSuccess={() => {
              closePanel()
            }}
            placeholder="Post an announcement for the crew..."
          />
        </div>
      </aside>
    )
  }

  if (selectedDetail.type === 'announcement-add') return null

  if (selectedDetail.type === 'trip-feed-add' && user) {
    return (
      <aside className={panelAsideClass}>
        <div className="flex items-center justify-between p-4 border-b border-earth-200">
          <h2 className="text-lg font-semibold text-foreground">Add Trip Feed Message</h2>
          <Button variant="ghost" size="icon" className={CLOSE_BUTTON_CLASS} onClick={closePanel} aria-label="Close panel">
            <X className="h-5 w-5" />
          </Button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          <TripFeedComposeForm
            onCreatePost={createPost}
            onSuccess={() => {
              closePanel()
            }}
          />
        </div>
      </aside>
    )
  }

  if (selectedDetail.type === 'trip-feed-add') return null

  if (selectedDetail.type === 'gear' && selectedGear) {
    const { totalConfirmedQuantity, remainingQuantity, userConfirmedQuantity } = getGearQuantities(
      selectedGear,
      user?.id
    )
    const maxForUser = Math.max(1, remainingQuantity + userConfirmedQuantity)
    const canEdit = Boolean(
      user &&
      (selectedGear.added_by_user_id === user.id || trip?.created_by === user.id)
    )
    const canDelete = canEdit
    const canClaim = Boolean(user)
    const gearClaims = selectedGear.claims ?? []
    const userClaim = gearClaims.find((c) => c.member.user?.id === user?.id)

    const handleGearSave = async (data: import('@/shared/api/gearNeeds').UpdateGearNeedRequest) => {
      await updateGearNeed(selectedGear.id, data)
      setIsEditingGear(false)
    }

    const handleDeleteGear = () => {
      confirm(
        {
          title: 'Delete gear need',
          description: `Are you sure you want to remove "${selectedGear.name}" from the trip?`,
          variant: 'destructive',
          confirmText: 'Delete'
        },
        async () => {
          setConfirmLoading(true)
          try {
            await deleteGearNeed(selectedGear.id)
            closePanel()
          } finally {
            setConfirmLoading(false)
          }
        }
      )
    }

    const handleClaim = (commitmentLevel: 'confirmed' | 'cant_bring') => {
      const safeQuantity =
        commitmentLevel === 'confirmed'
          ? Math.max(
              1,
              Math.min(
                typeof gearClaimQuantity === 'number' && gearClaimQuantity > 0
                  ? gearClaimQuantity
                  : 1,
                maxForUser
              )
            )
          : 1

      if (userClaim) {
        updateGearClaim(selectedGear.id, {
          commitment_level: commitmentLevel,
          quantity_claimed: commitmentLevel === 'confirmed' ? safeQuantity : undefined
        })
      } else {
        claimGearNeed(selectedGear.id, {
          commitment_level: commitmentLevel,
          quantity_claimed: commitmentLevel === 'confirmed' ? safeQuantity : undefined
        })
      }
    }

    const handleUnclaim = () => {
      unclaimGearNeed(selectedGear.id)
    }

    return (
      <>
        <aside
          className={panelAsideClass}
        >
          <div className="flex items-center justify-between p-4 border-b border-earth-200">
            <h2 className="text-lg font-semibold text-foreground">Gear Details</h2>
            <Button variant="ghost" size="icon" className={CLOSE_BUTTON_CLASS} onClick={closePanel} aria-label="Close panel">
              <X className="h-5 w-5" />
            </Button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {isEditingGear ? (
              <AddGearNeedForm
                onSave={handleGearSave}
                onCancel={() => setIsEditingGear(false)}
                isLoading={isUpdating}
                editingGearNeed={selectedGear}
                compact
              />
            ) : (
              <>
                <div className="flex items-center gap-2">
                  <Package className="h-5 w-5 text-earth-500" />
                  <h3 className="text-base font-medium text-earth-900">{selectedGear.name}</h3>
                </div>
                {selectedGear.is_essential && (
                  <Badge variant="destructive" className="text-xs">Essential</Badge>
                )}
                {selectedGear.description && (
                  <p className="text-sm text-earth-600">{selectedGear.description}</p>
                )}
                <Card className="border-earth-200">
                  <CardContent className="p-3">
                    <span className="text-xs font-medium text-earth-500">Category</span>
                    <p className="text-sm text-earth-900 mt-1 capitalize">{selectedGear.category?.replace('_', ' ') ?? '—'}</p>
                  </CardContent>
                </Card>
                <Card className="border-earth-200">
                  <CardContent className="p-3 space-y-1.5">
                    <span className="text-xs font-medium text-earth-500">Quantity</span>
                    <p className="text-sm text-earth-900">
                      {totalConfirmedQuantity}/{selectedGear.quantity_needed} claimed
                    </p>
                    {remainingQuantity <= 0 ? (
                      <p className="text-xs text-earth-500">All needed items are already covered.</p>
                    ) : (
                      <p className="text-xs text-earth-500">
                        {remainingQuantity} remaining to be claimed.
                      </p>
                    )}
                  </CardContent>
                </Card>
                {gearClaims.length > 0 && (
                  <div>
                    <span className="text-xs font-medium text-earth-500">
                      Claims ({totalConfirmedQuantity}/{selectedGear.quantity_needed})
                    </span>
                    <ul className="mt-2 space-y-2">
                      {gearClaims.map((claim) => (
                        <li key={claim.id} className="flex items-center justify-between text-sm">
                          <span className="text-earth-900">{claim.member.user?.name ?? 'Unknown'}</span>
                          <span
                            className={cn(
                              'inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-semibold',
                              claim.commitment_level === 'confirmed'
                                ? 'bg-success/20 text-success'
                                : 'bg-destructive/20 text-destructive-foreground'
                            )}
                          >
                            {claim.commitment_level === 'confirmed' ? (
                              <CheckCircle className="h-3 w-3" />
                            ) : (
                              <XCircle className="h-3 w-3" />
                            )}
                            {claim.commitment_level.replace('_', ' ')}
                            {claim.commitment_level === 'confirmed' && claim.quantity_claimed > 1 && (
                              <span className="ml-1 text-xs text-earth-700">x{claim.quantity_claimed}</span>
                            )}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {selectedGear.notes && (
                  <Card className="border-earth-200">
                    <CardContent className="p-3">
                      <span className="text-xs font-medium text-earth-500">Notes</span>
                      <p className="text-sm text-earth-600 mt-1">{selectedGear.notes}</p>
                    </CardContent>
                  </Card>
                )}

                {canClaim && (
                  <div className="space-y-3">
                    <span className="text-xs font-medium text-earth-500">Claim responsibility</span>
                    {user && (
                      <div className="flex items-center gap-3 max-w-xs">
                        <div className="flex flex-col gap-1">
                          <Label className="text-xs text-earth-500">Quantity you&apos;ll bring</Label>
                          <div className="flex items-center gap-1">
                            <Button
                              type="button"
                              size="icon"
                              variant="outline"
                              disabled={isClaiming || maxForUser <= 1}
                              onClick={() => {
                                const current =
                                  typeof gearClaimQuantity === 'number' && gearClaimQuantity > 0
                                    ? gearClaimQuantity
                                    : 1
                                const next = Math.max(1, current - 1)
                                setGearClaimQuantity(next)
                              }}
                              className="h-8 w-8"
                            >
                              <Minus className="h-3 w-3" />
                            </Button>
                            <div className="min-w-[2.5rem] text-center rounded border border-earth-300 px-2 py-1 text-sm bg-background">
                              {typeof gearClaimQuantity === 'number' && gearClaimQuantity > 0
                                ? gearClaimQuantity
                                : 'Qty'}
                            </div>
                            <Button
                              type="button"
                              size="icon"
                              variant="outline"
                              disabled={isClaiming || remainingQuantity + userConfirmedQuantity <= 0}
                              onClick={() => {
                                const current =
                                  typeof gearClaimQuantity === 'number' && gearClaimQuantity > 0
                                    ? gearClaimQuantity
                                    : 1
                                const next = Math.min(current + 1, maxForUser)
                                setGearClaimQuantity(next)
                              }}
                              className="h-8 w-8"
                            >
                              <Plus className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                        {remainingQuantity <= 0 && userConfirmedQuantity === 0 && (
                          <span className="text-xs text-earth-500">
                            All items are already covered.
                          </span>
                        )}
                      </div>
                    )}
                    {userClaim ? (
                      <div className="flex flex-wrap items-center gap-2">
                        <Button
                          size="sm"
                          variant="default"
                          onClick={() => handleClaim('confirmed')}
                          disabled={isClaiming || remainingQuantity + userConfirmedQuantity <= 0}
                          className="text-xs"
                        >
                          I&apos;ll Bring It
                        </Button>
                        {userClaim.commitment_level !== 'cant_bring' && (
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleClaim('cant_bring')}
                            disabled={isClaiming}
                            className="text-xs"
                          >
                            Can&apos;t Bring
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={handleUnclaim}
                          disabled={isClaiming}
                          className="text-xs border-border text-foreground hover:bg-muted/40"
                        >
                          Unclaim
                        </Button>
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        <Button
                          size="sm"
                          variant="default"
                          onClick={() => handleClaim('confirmed')}
                          disabled={isClaiming || remainingQuantity <= 0}
                          className="text-xs"
                        >
                          I&apos;ll Bring It
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleClaim('cant_bring')}
                          disabled={isClaiming}
                          className="text-xs"
                        >
                          Can&apos;t Bring
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {(canEdit || canDelete) && (
                  <div className="flex flex-col gap-2 pt-2 border-t border-earth-200">
                    {canEdit && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setIsEditingGear(true)}
                        disabled={isUpdating}
                        className="gap-2"
                      >
                        <Edit className="h-4 w-4" />
                        Edit
                      </Button>
                    )}
                    {canDelete && (
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={handleDeleteGear}
                        disabled={isDeleting}
                        className="gap-2"
                      >
                        <Trash2 className="h-4 w-4" />
                        Delete
                      </Button>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </aside>
        <ConfirmationDialog
          open={confirmState?.open ?? false}
          onOpenChange={(open) => !open && close()}
          title={confirmState?.title}
          description={confirmState?.description ?? ''}
          confirmText={confirmState?.confirmText}
          cancelText={confirmState?.cancelText}
          variant={confirmState?.variant}
          onConfirm={confirmState?.onConfirm ?? (() => {})}
          isLoading={confirmState?.isLoading}
        />
      </>
    )
  }

  if (selectedDetail.type === 'gear' && !selectedGear) return null

  if (selectedDetail.type === 'meal' && selectedMeal) {
    const contributorName = getMealContributorName(selectedMeal)
    const dayLabel = selectedMeal.planned_date ? formatDateForDisplay(selectedMeal.planned_date) : '—'
    const canEditMeal = Boolean(
      user && (selectedMeal.created_by_user_id === user.id || trip?.created_by === user.id)
    )

    const handleMealSave = async (data: import('@/shared/api/mealNeeds').UpdateMealNeedRequest) => {
      await updateMeal(selectedMeal.id, data)
      setIsEditingMeal(false)
    }

    const handleDeleteMeal = () => {
      confirm(
        {
          title: 'Delete meal',
          description: `Are you sure you want to remove "${selectedMeal.name}" from the trip?`,
          variant: 'destructive',
          confirmText: 'Delete'
        },
        async () => {
          setConfirmLoading(true)
          try {
            await deleteMeal(selectedMeal.id)
            closePanel()
          } finally {
            setConfirmLoading(false)
          }
        }
      )
    }

    return (
      <>
        <aside
          className={panelAsideClass}
        >
          <div className="flex items-center justify-between p-4 border-b border-earth-200">
            <h2 className="text-lg font-semibold text-foreground">Meal Details</h2>
            <Button variant="ghost" size="icon" className={CLOSE_BUTTON_CLASS} onClick={closePanel} aria-label="Close panel">
              <X className="h-5 w-5" />
            </Button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {isEditingMeal ? (
              <AddMealForm
                onSave={handleMealSave}
                onCancel={() => setIsEditingMeal(false)}
                isLoading={isMealUpdating}
                editingMeal={selectedMeal}
                tripId={tripId}
                tripStartDate={trip?.start_date}
                tripEndDate={trip?.end_date}
                compact
              />
            ) : (
              <>
                <div className="flex items-center gap-2">
                  <Utensils className="h-5 w-5 text-earth-500" />
                  <h3 className="text-base font-medium text-earth-900">{selectedMeal.name}</h3>
                </div>
                <Badge variant="secondary" className="capitalize">{selectedMeal.planned_meal_time}</Badge>
                <Card className="border-earth-200">
                  <CardContent className="p-3">
                    <span className="text-xs font-medium text-earth-500">Date</span>
                    <p className="text-sm text-earth-900 mt-1">{dayLabel}</p>
                  </CardContent>
                </Card>
                <Card className="border-earth-200">
                  <CardContent className="p-3">
                    <span className="text-xs font-medium text-earth-500">Assigned to</span>
                    <p className="text-sm text-earth-900 mt-1">{contributorName}</p>
                  </CardContent>
                </Card>
                {(selectedMeal.total_members != null || selectedMeal.eating_count != null) && (
                  <Card className="border-earth-200">
                    <CardContent className="p-3">
                      <span className="text-xs font-medium text-earth-500">Eating</span>
                      <p className="text-sm text-earth-900 mt-1">
                        {selectedMeal.eating_count ?? '—'} / {selectedMeal.total_members ?? '—'}
                      </p>
                      {user && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="mt-2 gap-1.5 text-xs"
                          disabled={isMealOptingOut}
                          onClick={() => {
                            if (selectedMeal.current_user_opted_out) {
                              optInToMeal(selectedMeal.id)
                            } else {
                              optOutOfMeal(selectedMeal.id)
                            }
                          }}
                          aria-label={selectedMeal.current_user_opted_out ? 'Opt in' : 'Opt out'}
                        >
                          {selectedMeal.current_user_opted_out ? (
                            <>
                              <UserPlus className="h-3.5 w-3.5" />
                              Opt in
                            </>
                          ) : (
                            <>
                              <UserMinus className="h-3.5 w-3.5" />
                              Opt out
                            </>
                          )}
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                )}
                {selectedMeal.serves_count != null && (
                  <Card className="border-earth-200">
                    <CardContent className="p-3">
                      <span className="text-xs font-medium text-earth-500">Serves</span>
                      <p className="text-sm text-earth-900 mt-1">{selectedMeal.serves_count}</p>
                    </CardContent>
                  </Card>
                )}
                {selectedMeal.description && (
                  <p className="text-sm text-earth-600">{selectedMeal.description}</p>
                )}
                {selectedMeal.ingredients && selectedMeal.ingredients.length > 0 && (
                  <div>
                    <span className="text-xs font-medium text-earth-500">Ingredients</span>
                    <ul className="mt-1 list-disc list-inside text-sm text-earth-700 space-y-0.5">
                      {selectedMeal.ingredients.map((ing, i) => {
                        const conflict = (selectedMeal.allergyAlerts ?? []).some(a =>
                          a.conflictingItems.some(ci => ci.toLowerCase().trim() === ing.toLowerCase().trim())
                        )
                        return (
                          <li
                            key={i}
                            className={conflict ? 'list-none flex items-center gap-1.5 text-red-700 font-medium' : ''}
                          >
                            {conflict && <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />}
                            {ing}
                          </li>
                        )
                      })}
                    </ul>
                  </div>
                )}
                {selectedMeal.allergyAlerts && selectedMeal.allergyAlerts.length > 0 && (
                  <Card className="border-red-200 bg-red-50">
                    <CardContent className="p-3">
                      <span className="text-xs font-medium text-red-600 flex items-center gap-1.5">
                        <AlertTriangle className="h-3.5 w-3.5" />
                        Who can't eat this
                      </span>
                      <ul className="mt-2 space-y-1.5">
                        {selectedMeal.allergyAlerts.map((alert) => (
                          <li key={alert.userId} className="text-sm text-red-800">
                            <span className="font-semibold">{alert.userName}</span>
                            <span className="text-red-700"> — allergic to {alert.conflictingItems.join(', ')}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}
                {selectedMeal.notes && (
                  <Card className="border-earth-200">
                    <CardContent className="p-3">
                      <span className="text-xs font-medium text-earth-500">Notes</span>
                      <p className="text-sm text-earth-600 mt-1">{selectedMeal.notes}</p>
                    </CardContent>
                  </Card>
                )}
                {canEditMeal && (
                  <div className="flex flex-col gap-2 pt-2 border-t border-earth-200">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setIsEditingMeal(true)}
                      disabled={isMealUpdating}
                      className="gap-2"
                    >
                      <Edit className="h-4 w-4" />
                      Edit
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={handleDeleteMeal}
                      disabled={isMealDeleting}
                      className="gap-2"
                    >
                      <Trash2 className="h-4 w-4" />
                      Delete
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </aside>
        <ConfirmationDialog
          open={confirmState?.open ?? false}
          onOpenChange={(open) => !open && close()}
          title={confirmState?.title}
          description={confirmState?.description ?? ''}
          confirmText={confirmState?.confirmText}
          cancelText={confirmState?.cancelText}
          variant={confirmState?.variant}
          onConfirm={confirmState?.onConfirm ?? (() => {})}
          isLoading={confirmState?.isLoading}
        />
      </>
    )
  }

  if (selectedDetail.type === 'meal' && !selectedMeal) return null

  if (selectedDetail.type === 'waypoint' && !waypoint) {
    if (isOverviewLoading) {
      return (
        <aside
          className={panelAsideClass}
        >
          <div className="flex items-center justify-between p-4 border-b border-earth-200">
            <h2 className="text-lg font-semibold text-foreground">Itinerary Details</h2>
            <Button variant="ghost" size="icon" className={CLOSE_BUTTON_CLASS} onClick={closePanel} aria-label="Close panel">
              <X className="h-5 w-5" />
            </Button>
          </div>
          <div className="flex-1 overflow-y-auto p-4">
            <div className="animate-pulse space-y-4">
              <div className="h-5 w-24 rounded bg-earth-200" />
              <div className="h-6 w-48 rounded bg-earth-200" />
              <div className="h-4 w-full rounded bg-earth-100" />
              <div className="h-4 w-3/4 rounded bg-earth-100" />
            </div>
          </div>
        </aside>
      )
    }
    return null
  }

  if (selectedDetail.type === 'waypoint' && waypoint) {
    const locationText = waypoint.poi_address ?? (waypoint.latitude != null && waypoint.longitude != null
      ? formatCoordinatesForCopy(waypoint.latitude, waypoint.longitude)
      : '—')
    const TypeIcon = getWaypointTypeIconWithCustom(waypoint.poi_type ?? null, waypoint.poi_custom_label)
    const typeLabel = getWaypointTypeLabel(waypoint.poi_type ?? null, waypoint.poi_custom_label)

    const notifyMainContentRefresh = () => {
      onWaypointDeletedRef.current?.()
    }

    const displayName =
      waypoint.name && waypoint.name.trim().length > 0 ? waypoint.name : 'Unnamed waypoint'

    const handleSaveWaypointName = async () => {
      if (!isOwner) {
        setIsEditingWaypointName(false)
        return
      }
      const trimmed = waypointNameDraft.trim()
      const current = waypoint.name ?? ''
      if (trimmed === current) {
        setIsEditingWaypointName(false)
        return
      }
      setIsUpdatingWaypoint(true)
      try {
        await updateWaypoint(waypoint.id, { name: trimmed })
        notifyMainContentRefresh()
      } finally {
        setIsUpdatingWaypoint(false)
        setIsEditingWaypointName(false)
      }
    }

    const handleTimeChange = async (field: 'start_time' | 'end_time', value: string | null) => {
      const normalized = value ? normalizeTimeToHHMM(value) : null
      setIsUpdatingWaypoint(true)
      try {
        await updateWaypoint(waypoint.id, { [field]: normalized })
        notifyMainContentRefresh()
      } finally {
        setIsUpdatingWaypoint(false)
      }
    }

    const handleNotesChange = async (value: string | null) => {
      setIsUpdatingWaypoint(true)
      try {
        await updateWaypoint(waypoint.id, { notes: value ?? undefined })
        notifyMainContentRefresh()
      } finally {
        setIsUpdatingWaypoint(false)
      }
    }

    const handleTypeSelect = async (poiType: POIType, poiCustomLabel?: string) => {
      setIsUpdatingWaypoint(true)
      try {
        await updateWaypoint(waypoint.id, {
          poi_type: poiType ?? undefined,
          poi_custom_label: poiCustomLabel ?? undefined
        })
        notifyMainContentRefresh()
      } finally {
        setIsUpdatingWaypoint(false)
      }
    }

    const handleDeleteWaypoint = () => {
      if (!deleteWaypoint) return
      confirm(
        {
          title: 'Delete waypoint',
          description: `Remove "${waypoint.name || 'Unnamed waypoint'}" from the itinerary?`,
          variant: 'destructive',
          confirmText: 'Delete',
        },
        async () => {
          setConfirmLoading(true)
          try {
            await deleteWaypoint(waypoint.id)
            closePanel()
            await onWaypointDeletedRef.current?.()
          } finally {
            setConfirmLoading(false)
            close()
          }
        }
      )
    }

    return (
      <>
      <aside
        className={panelAsideClass}
      >
        <div className="flex items-center justify-between p-4 border-b border-earth-200">
          <h2 className="text-lg font-semibold text-foreground">Itinerary Details</h2>
          <Button variant="ghost" size="icon" className={CLOSE_BUTTON_CLASS} onClick={closePanel} aria-label="Close panel">
            <X className="h-5 w-5" />
          </Button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          <Badge variant="secondary" className="bg-adventure-100 text-adventure-800">
            Day {dayNumber}
          </Badge>
          {isOwner ? (
            <div className="space-y-1">
              {isEditingWaypointName ? (
                <Input
                  value={waypointNameDraft}
                  onChange={(e) => setWaypointNameDraft(e.target.value)}
                  onBlur={() => {
                    void handleSaveWaypointName()
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      void handleSaveWaypointName()
                    } else if (e.key === 'Escape') {
                      e.preventDefault()
                      setWaypointNameDraft(waypoint.name ?? '')
                      setIsEditingWaypointName(false)
                    }
                  }}
                  disabled={isUpdatingWaypoint}
                  placeholder="Waypoint title"
                  className="text-base font-medium"
                  autoFocus
                />
              ) : (
                <>
                  <button
                    type="button"
                    className="inline-flex max-w-full items-center gap-1.5 rounded-md border border-transparent px-2 py-1 -ml-1 text-base font-medium text-foreground hover:border-earth-300 hover:bg-earth-100/70 transition-colors"
                    onClick={() => {
                      if (!isUpdatingWaypoint) {
                        setIsEditingWaypointName(true)
                      }
                    }}
                    title="Click to rename waypoint"
                  >
                    <span className="truncate max-w-[220px]">{displayName}</span>
                    <Edit className="h-4 w-4 text-earth-500 flex-shrink-0" aria-hidden />
                  </button>
                </>
              )}
            </div>
          ) : (
            <h3 className="text-base font-medium text-foreground">
              {displayName}
            </h3>
          )}

          {(waypoint.start_time || waypoint.end_time) && (() => {
            const arrive = waypoint.start_time ? formatTimeForDisplay(waypoint.start_time) : null
            const leave = waypoint.end_time ? formatTimeForDisplay(waypoint.end_time) : null
            const durationMinutes = calculateDurationMinutes(waypoint.start_time, waypoint.end_time)
            const durationLabel = durationMinutes != null ? formatDuration(durationMinutes) : null
            return (
              <Card className="border-earth-200 bg-earth-50">
                <CardContent className="p-3 space-y-1.5">
                  <div className="flex items-center gap-2 text-sm">
                    <Clock className="h-4 w-4 text-earth-500 flex-shrink-0" />
                    <span className="text-earth-700 font-medium">
                      {arrive && leave && (
                        <>Arrive {arrive} · Leave {leave}</>
                      )}
                      {arrive && !leave && <>Arrive {arrive}</>}
                      {!arrive && leave && <>Leave by {leave}</>}
                    </span>
                  </div>
                  {durationLabel != null && (
                    <p className="text-xs text-muted-foreground pl-6">
                      {durationLabel} at this stop
                    </p>
                  )}
                </CardContent>
              </Card>
            )
          })()}

          {isOwner ? (
            <>
              <div className="space-y-3">
                <div className="space-y-1">
                  <Label className="text-xs font-medium text-earth-500">Start time</Label>
                  <WaypointTimeInput
                    value={waypoint.start_time ?? null}
                    onChange={(value) => handleTimeChange('start_time', value)}
                    placeholder="Set start time"
                    className="w-full"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-medium text-earth-500">End time</Label>
                  <WaypointTimeInput
                    value={waypoint.end_time ?? null}
                    onChange={(value) => handleTimeChange('end_time', value)}
                    placeholder="Set end time"
                    className="w-full"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <div className="text-label text-earth-900">Notes</div>
                <WaypointNotesEditor
                  value={waypoint.notes ?? null}
                  onChange={handleNotesChange}
                  placeholder="Add notes about this stop..."
                  disabled={isUpdatingWaypoint}
                />
              </div>

              <div className="space-y-1">
                <Label className="text-xs font-medium text-earth-500">Waypoint type</Label>
                <WaypointTypeDropdown
                  onSelect={handleTypeSelect}
                  align="start"
                  trigger={
                    <Button variant="outline" size="sm" className="w-full justify-start gap-2" disabled={isUpdatingWaypoint}>
                      <TypeIcon className="h-4 w-4" />
                      {typeLabel || 'Select type'}
                    </Button>
                  }
                />
              </div>

              <div className="flex flex-col gap-2 pt-2 border-t border-earth-200">
                <Button
                  variant="destructive"
                  size="sm"
                  className="gap-2"
                  onClick={handleDeleteWaypoint}
                  disabled={!deleteWaypoint || isUpdatingWaypoint}
                >
                  <Trash2 className="h-4 w-4" />
                  Delete waypoint
                </Button>
              </div>
            </>
          ) : (
            <>
              <p className="text-sm text-earth-600">
                {waypoint.notes || 'No description.'}
              </p>
            </>
          )}

          <div className="grid gap-3">
            <button
              type="button"
              disabled={locationText === '—'}
              onClick={() => {
                if (locationText === '—' || locationText == null) return
                navigator.clipboard.writeText(locationText).then(() => {
                  setCopiedLocation(true)
                  setTimeout(() => setCopiedLocation(false), 2000)
                })
              }}
              className="w-full text-left rounded-lg border border-earth-200 bg-card transition-colors hover:bg-earth-100 disabled:cursor-default disabled:hover:bg-card group"
              title={locationText !== '—' ? 'Click to copy' : undefined}
            >
              <div className="p-3 flex items-center gap-2">
                <MapPin className="h-4 w-4 text-earth-500 flex-shrink-0" />
                <span className="text-xs font-medium text-muted-foreground">Location</span>
                <span className="text-sm text-foreground ml-auto truncate" title={locationText ?? undefined}>{locationText}</span>
                {locationText !== '—' && (
                  <span className="flex-shrink-0 text-earth-400 group-hover:text-earth-600 transition-colors">
                    {copiedLocation
                      ? <Check className="h-3.5 w-3.5 text-success" />
                      : <Copy className="h-3.5 w-3.5" />
                    }
                  </span>
                )}
              </div>
            </button>
            <Card className="border-earth-200">
              <CardContent className="p-3 flex items-center gap-2">
                <Navigation className="h-4 w-4 text-earth-500 flex-shrink-0" />
                <span className="text-xs font-medium text-muted-foreground">Distance</span>
                <span className="text-sm text-foreground ml-auto">{distanceFromPrevious ?? '—'}</span>
              </CardContent>
            </Card>
          </div>
        </div>
      </aside>
        <ConfirmationDialog
          open={confirmState?.open ?? false}
          onOpenChange={(open) => !open && close()}
          title={confirmState?.title}
          description={confirmState?.description ?? ''}
          confirmText={confirmState?.confirmText}
          cancelText={confirmState?.cancelText}
          variant={confirmState?.variant}
          onConfirm={confirmState?.onConfirm ?? (() => {})}
          isLoading={confirmState?.isLoading}
        />
      </>
    )
  }

  return null
}
